import { z } from 'zod';

import { TimestampSchema } from './primitives.js';

/**
 * A published peer benchmark (gap G24).
 *
 * The ONLY cross-tenant figure any client is ever allowed to read. One document
 * per metric and size band, holding a rounded median and a participant count —
 * never a list, never a maximum, never a tenant id.
 *
 * Written exclusively by the `publishPeerBenchmarks` scheduled job with admin
 * credentials. It is world-readable by signed-in operators on purpose: there is
 * nothing identifying in it, and gating it per tenant would mean a per-tenant
 * copy of the same aggregate for no privacy gain.
 */
export const PeerBenchmarkMetricSchema = z
  .enum(['avgTicketSen', 'washesPerDay'])
  .describe('Which figure this benchmark compares.');
export type PeerBenchmarkMetric = z.infer<typeof PeerBenchmarkMetricSchema>;

export const PeerSizeBandSchema = z
  .enum(['single', 'small', 'medium', 'large'])
  .describe('Outlet-count band, so a one-bay shop is not measured against a chain.');
export type PeerSizeBand = z.infer<typeof PeerSizeBandSchema>;

export const PeerBenchmarkDocSchema = z.object({
  id: z.string().describe('Doc id — `{metric}:{sizeBand}`.'),
  metric: PeerBenchmarkMetricSchema,
  sizeBand: PeerSizeBandSchema,
  platformBrandId: z.string().min(1).describe('Benchmarks never cross platform brands.'),
  /**
   * Cohort median, ALREADY ROUNDED by the publisher so it is not any single
   * business's exact figure.
   */
  peerMedian: z.number().nonnegative().describe('Rounded cohort median.'),
  /**
   * Participating businesses behind the median.
   *
   * The publisher refuses to write a document below the anonymity floor, and
   * the client re-checks it anyway — a document written by an older deploy must
   * not be able to produce a two-business "median".
   */
  cohortSize: z.number().int().nonnegative().describe('How many businesses contributed.'),
  updatedAt: TimestampSchema.describe('When the job last recomputed this.'),
});
export type PeerBenchmarkDoc = z.infer<typeof PeerBenchmarkDocSchema>;
